import React, { useCallback, useState } from "react";

import { Autocomplete, AutocompleteSearchData, DatePicker, Select, SelectData, SelectEvent, TextField } from "@king-ict/eupisi-ui/components";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { DocumentUploadRequest, getMunicipalityById, getTownById, searchMunicipalities, searchTowns, uploadDocument } from "../../api/nisooApi";
import { TownSelect } from "../../components/FormControls/TownSelect";
import UploadButton from "../../components/UploadButton";
import { DocumentType } from "../../models/Document";
import { MunicipalityCodeBook } from "../../models/Municipality";
import { OtherLegalEntityLocation } from "../../models/ProgramRequest";
import { TownCodeBook } from "../../models/Town";
import { useGetCountiesQuery } from "../../queries/codeBookQuery";
import styles from "./ProgramRequestEdit.module.scss";

export interface OtherLegalEntityLocationProps {
  name: string;
  otherLegalEntityLocation: OtherLegalEntityLocation;
  getErrorForPath: (path: string) => string | undefined;
  onChange: (e: any) => void;
}

export function OtherLegalEntityInfo({
  name,
  otherLegalEntityLocation,
  getErrorForPath,
  onChange
}: OtherLegalEntityLocationProps) {
  const { t } = useTranslation();

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    const newOtherLegalEntityLocationInfo: OtherLegalEntityLocation = {
      ...otherLegalEntityLocation,
      name: val && val.length > 0 ? val : "",
    };
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newOtherLegalEntityLocationInfo
      }
    });
  };

  const handleProgramExecutionRequestDocumentAdded = async (file: File, event: React.ChangeEvent<HTMLInputElement>) => {   

    const newOtherLegalEntityLocationInfo: OtherLegalEntityLocation = {
      ...otherLegalEntityLocation,
      programExecutionRequestDocument: {
        id: -1,
        fileName: file.name,
        file: file
      },
    };
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newOtherLegalEntityLocationInfo
      }
    });    
  };

  const handleProgramExecutionRequestDocumentRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const programExecutionRequestDocument = otherLegalEntityLocation.programExecutionRequestDocument;
    if (programExecutionRequestDocument === undefined || programExecutionRequestDocument.id === undefined) {
      return;
    }
          
    const newOtherLegalEntityLocationInfo: OtherLegalEntityLocation = {
      ...otherLegalEntityLocation,
      programExecutionRequestDocument: undefined
    };    
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newOtherLegalEntityLocationInfo
      }
    });
  };

  const handleMinistryConsentDocumentAdded = async (file: File, event: React.ChangeEvent<HTMLInputElement>) => {    

    const newOtherLegalEntityLocationInfo: OtherLegalEntityLocation = {
      ...otherLegalEntityLocation,
      ministryConsentDocument: {
        id: -1,
        fileName: file.name,
        file: file
      },
    };
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newOtherLegalEntityLocationInfo
      }
    });
  };

  const handleMinistryConsentDocumentRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const ministryConsentDocument = otherLegalEntityLocation.ministryConsentDocument;
    if (ministryConsentDocument === undefined || ministryConsentDocument.id === undefined) {
      return;
    }
          
    const newOtherLegalEntityLocationInfo: OtherLegalEntityLocation = {
      ...otherLegalEntityLocation,
      ministryConsentDocument: undefined
    };
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newOtherLegalEntityLocationInfo
      }
    });
  };

  const handlePersonnelConditionsDocumentAdded = async (file: File, event: React.ChangeEvent<HTMLInputElement>) => {
    const newOtherLegalEntityLocationInfo: OtherLegalEntityLocation = {
      ...otherLegalEntityLocation,
      personnelConditionsDocument: {
        id: -1,
        fileName: file.name,
        file: file
      },
    };
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newOtherLegalEntityLocationInfo
      }
    });
  };

  const handlePersonnelConditionsDocumentRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const personnelConditionsDocument = otherLegalEntityLocation.personnelConditionsDocument;
    if (personnelConditionsDocument === undefined || personnelConditionsDocument.id === undefined) {
      return;
    }
          
    const newOtherLegalEntityLocationInfo: OtherLegalEntityLocation = {
      ...otherLegalEntityLocation,
      personnelConditionsDocument: undefined
    };
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newOtherLegalEntityLocationInfo
      }
    });
  };

  const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    const newOtherLegalEntityLocationInfo: OtherLegalEntityLocation = {
      ...otherLegalEntityLocation,
      address: val && val.length > 0 ? val : "",
    };
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newOtherLegalEntityLocationInfo
      }
    });
  }

  const handleTownChange = (townCodeBook: TownCodeBook) => {    
    const newOtherLegalEntityLocationInfo: OtherLegalEntityLocation = {
      ...otherLegalEntityLocation,
      town: townCodeBook,
    };
    onChange({
      target: {
        name: name,
        value: newOtherLegalEntityLocationInfo,
      }
    });       
  };

  const handleDateFromChange = (event:  React.ChangeEvent<any>) => {
    const val = new Date(event.target.value);
    const newOtherLegalEntityLocationInfo: OtherLegalEntityLocation = {
      ...otherLegalEntityLocation,
      dateFrom: val,
    };
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newOtherLegalEntityLocationInfo,
      }
    });
  };

  const handleDateToChange = (event:  React.ChangeEvent<any>) => {
    const val = new Date(event.target.value);
    const newOtherLegalEntityLocationInfo: OtherLegalEntityLocation = {
      ...otherLegalEntityLocation,
      dateTo: val,
    };
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newOtherLegalEntityLocationInfo,
      }
    });
  };

  return (
    <>
      <Row>
        <Col md={12}>
          <TextField
            label={t("otherLegalEntityLocation.legalEntityName")}
            value={otherLegalEntityLocation.name}
            maxLength={255}
            onChange={handleNameChange}
            errorText={getErrorForPath(`${name}.name`)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <UploadButton
            label={t("otherLegalEntityLocation.programExecutionRequest")}
            className={styles.uploadButtonInRowSection}
            onFileAdded={handleProgramExecutionRequestDocumentAdded}
            onFileRemoved={handleProgramExecutionRequestDocumentRemoved}
            value={otherLegalEntityLocation.programExecutionRequestDocument}
            fileFilter={t("document.imagesAndPdfFilter")}
            errorText={getErrorForPath(`${name}.programExecutionRequestDocument`)}
            documentType={DocumentType.OtherLegalEntityProgramExecutionRequest}
          />
        </Col>
        <Col md={6}>
          <UploadButton
            label={t("otherLegalEntityLocation.ministryConsent")}
            className={styles.uploadButtonInRowSection}
            onFileAdded={handleMinistryConsentDocumentAdded}
            onFileRemoved={handleMinistryConsentDocumentRemoved}
            value={otherLegalEntityLocation.ministryConsentDocument}
            fileFilter={t("document.imagesAndPdfFilter")}
            errorText={getErrorForPath(`${name}.ministryConsentDocument`)}
            documentType={DocumentType.OtherLegalEntityMinistryConsent}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <UploadButton
            label={t("otherLegalEntityLocation.personnelConditions")}
            className={styles.uploadButtonInRowSection}
            onFileAdded={handlePersonnelConditionsDocumentAdded}
            onFileRemoved={handlePersonnelConditionsDocumentRemoved}          
            value={otherLegalEntityLocation.personnelConditionsDocument}
            fileFilter={t("document.imagesAndPdfFilter")}
            errorText={getErrorForPath(`${name}.personnelConditionsDocument`)}
            documentType={DocumentType.OtherLegalEntityPersonnelConditions}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <TownSelect
            initialValue={otherLegalEntityLocation.town?.id}
            handleChange={handleTownChange}
            errorText={getErrorForPath(`${name}.town`)}
            getErrorForPath={getErrorForPath}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <TextField
            label={t("otherLegalEntityLocation.addressOtherLegalEntityLocation")}
            value={otherLegalEntityLocation.address}
            maxLength={255}
            onChange={handleAddressChange}
            errorText={getErrorForPath(`${name}.address`)}
          />
        </Col>
{/*         <Col md={4}>
          <Autocomplete
            label={t("request.townOtherLegalEntityLocation")}
            value={otherLegalEntityLocation.townId}
            onChange={handleTownChange}
            getOptions={handleTownSearch}
            errorText={getErrorForPath(`townId`)}
          />
        </Col>
        <Col md={4}>
          <Autocomplete
            label={t("request.municipalityOtherLegalEntityLocation")}
            value={municipalityId}
            onChange={handleMunicipalityChange}
            getOptions={handleMunicipalitySearch}
          />
        </Col>
 */}
      </Row>
{/*       <Row>
        <Col md={4}>
          <Select
            label={t("request.countyOtherLegalEntityLocation")}
            name={"countyId"}
            value={countyId}
            emptyOption={{ text: "", value: undefined }}
            onChange={handleCountyChange}
            selectData={codeBookToSelectData(counties)}
          />
        </Col>
      </Row>
 */}      
      <Row>
        <Col md={6}>
          <DatePicker
            label={t("common.dateFrom")}
            name={"dateFrom"}
            value={otherLegalEntityLocation.dateFrom}
            onChange={handleDateFromChange}
            errorText={getErrorForPath(`${name}.dateFrom`)}
            minYear={new Date().getFullYear() - 25}
            maxYear={new Date().getFullYear() + 25}
          />
        </Col>
        <Col md={6}>
          <DatePicker
            label={t("common.dateTo")}
            name={"dateTo"}
            value={otherLegalEntityLocation.dateTo}
            onChange={handleDateToChange}
            errorText={getErrorForPath(`${name}.dateTo`)}
            minYear={new Date().getFullYear() - 25}
            maxYear={new Date().getFullYear() + 25}
          />
        </Col>
      </Row>
    </>
  );
}